import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { AlertTriangle, RotateCcw, Loader2, Eye, CheckCircle2, XCircle } from "lucide-react";

const PHASES = {
  starting: "Starting…", pausing_autoban: "Pausing auto-ban", lifting_bans: "Lifting automatic Discord bans",
  wiping_data: "Wiping blacklist data & caches", restarting_bot: "Restarting bot & re-registering commands",
  rescanning: "Pulling fresh server lists & rescanning", done: "Done", failed: "Failed",
};

function Stat({ label, value, tone = "wipe", testId }) {
  const color = tone === "keep" ? "text-emerald-400" : tone === "lift" ? "text-yellow-300" : "text-[var(--ff-red)]";
  return (
    <div className="bg-[var(--ff-surface)] p-4" data-testid={testId}>
      <div className="label-eyebrow mb-1">{label}</div>
      <div className={`font-display text-2xl ${color}`}>{value}</div>
    </div>
  );
}

function NameList({ title, rows, testId }) {
  if (!rows?.length) return null;
  return (
    <details className="border border-[var(--ff-border)] p-3" data-testid={testId}>
      <summary className="cursor-pointer font-mono-ui text-xs uppercase tracking-[0.15em]">{title} ({rows.length})</summary>
      <div className="mt-2 max-h-48 overflow-y-auto space-y-1">
        {rows.map((r) => (
          <div key={r.discord_id || r.user_id} className="text-xs font-mono-ui flex gap-2">
            <span className="text-white shrink-0">{r.username || r.discord_id}</span>
            <span className="text-[var(--ff-text-dim)] truncate">{r.reason}</span>
          </div>
        ))}
      </div>
    </details>
  );
}

function ResetPreview({ p }) {
  const c = p.counts;
  return (
    <div className="space-y-3" data-testid="reset-preview">
      {p.bans_error && <div className="text-xs text-yellow-300 font-mono-ui" data-testid="reset-bans-error">Couldn&apos;t read Discord bans: {p.bans_error}</div>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--ff-border)]">
        <Stat label="Server blacklist" value={c.blacklisted_guilds} testId="reset-stat-guilds" />
        <Stat label="User blacklist" value={c.blacklisted_users} testId="reset-stat-users" />
        <Stat label="Ban log rows" value={c.ban_log} testId="reset-stat-banlog" />
        <Stat label="Mod history" value={c.mod_actions} testId="reset-stat-modactions" />
        <Stat label="Saved server lists" value={c.users_with_guild_cache} testId="reset-stat-guildcache" />
        <Stat label="Discord bans to lift" value={`${p.to_lift.length} / ${p.discord_bans_total}`} tone="lift" testId="reset-stat-lift" />
        <Stat label="Website flags to clear" value={p.flags_clear.length} tone="lift" testId="reset-stat-flags" />
        <Stat label="Whitelist (kept)" value={c.whitelisted_kept} tone="keep" testId="reset-stat-whitelist" />
      </div>
      <NameList title="Will be unbanned on Discord" rows={p.to_lift} testId="reset-list-lift" />
      <NameList title="Website ban flags to clear" rows={p.flags_clear} testId="reset-list-flags" />
      <NameList title="Kept — added to the blacklist by name" rows={p.kept_direct} testId="reset-list-kept" />
    </div>
  );
}

function ResetOptions({ opts, setOpts, keptCount }) {
  const set = (patch) => setOpts((o) => ({ ...o, ...patch }));
  return (
    <div className="space-y-3 text-sm">
      <label className="flex items-start gap-2 cursor-pointer">
        <input type="checkbox" className="mt-1" checked={opts.lift_direct_list_bans} onChange={(e) => set({ lift_direct_list_bans: e.target.checked })} data-testid="reset-lift-direct-toggle" />
        <span>Also lift bans for people added to the blacklist <b>by name</b>{keptCount != null ? ` (${keptCount})` : ""}. <span className="text-[var(--ff-text-dim)]">Off = treat them as hand-made bans and keep them.</span></span>
      </label>
      <label className="flex items-start gap-2 cursor-pointer">
        <input type="checkbox" className="mt-1" checked={opts.rescan} onChange={(e) => set({ rescan: e.target.checked })} data-testid="reset-rescan-toggle" />
        <span>Pull fresh server lists for everyone and rescan</span>
      </label>
      <div className="flex flex-wrap gap-4" data-testid="reset-autoban-choice">
        <span className="label-eyebrow self-center">After reset, auto-ban:</span>
        {[["paused", "Paused (review first)"], ["on", "On (resume bans)"]].map(([v, l]) => (
          <label key={v} className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="autoban_after" checked={opts.autoban_after === v} onChange={() => set({ autoban_after: v })} data-testid={`reset-autoban-${v}`} />
            {l}
          </label>
        ))}
      </div>
    </div>
  );
}

function ResetStatus({ job, running }) {
  if (!job) return null;
  const r = job.results || {};
  const pct = job.progress?.total ? Math.round((job.progress.done / job.progress.total) * 100) : null;
  return (
    <div className="border border-[var(--ff-border)] p-4 space-y-2 font-mono-ui text-xs" data-testid="reset-status">
      <div className="flex items-center gap-2">
        {running ? <Loader2 size={14} className="animate-spin text-yellow-300" /> : job.status === "done" ? <CheckCircle2 size={14} className="text-emerald-400" /> : <XCircle size={14} className="text-[var(--ff-red)]" />}
        <span className="uppercase tracking-[0.15em]" data-testid="reset-phase">{PHASES[job.phase] || job.phase}</span>
        <span className="text-[var(--ff-text-dim)] ml-auto">{job.by} · {new Date(job.started_at).toLocaleString()}</span>
      </div>
      {running && job.phase === "rescanning" && pct != null && (
        <div className="h-1.5 bg-[var(--ff-bg)]"><div className="h-full bg-[var(--ff-red)] transition-[width]" style={{ width: `${pct}%` }} /></div>
      )}
      {job.status === "failed" && <div className="text-[var(--ff-red)]">{job.error}</div>}
      {job.status === "done" && (
        <div className="text-[var(--ff-text-dim)] leading-relaxed" data-testid="reset-results">
          Lifted {r.discord_unbanned?.length || 0} Discord ban(s){r.discord_unban_failed?.length ? `, ${r.discord_unban_failed.length} failed` : ""} ·
          cleared {r.local_flags_cleared?.length || 0} website flag(s) · wiped {r.wiped?.blacklisted_guilds || 0} server + {r.wiped?.blacklisted_users || 0} user blacklist entries,
          {" "}{r.wiped?.ban_log || 0} ban-log rows, {r.wiped?.mod_actions || 0} mod-history rows · voice bot {r.voice_bot_restarted ? "restarted" : "not restarted"}
          {r.rescan ? ` · rescanned ${r.rescan.scanned} users` : ""} · auto-ban <b className="text-white">{r.autoban_enabled ? "ON" : "PAUSED"}</b>
        </div>
      )}
    </div>
  );
}

export default function DiscordResetPanel({ onDone }) {
  const { user } = useAuth();
  const allowed = ["founder", "executive"].includes(user?.role);
  const [opts, setOpts] = useState({ lift_direct_list_bans: false, rescan: true, autoban_after: "paused" });
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [job, setJob] = useState(null);
  const [running, setRunning] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/discord-reset/status");
      setJob(data.job);
      setRunning(data.running);
      return data.running;
    } catch { return false; }
  }, []);

  useEffect(() => { if (allowed) loadStatus(); }, [allowed, loadStatus]);

  useEffect(() => {
    if (!running) return;
    const t = setInterval(async () => {
      if (!(await loadStatus())) { clearInterval(t); onDone && onDone(); }
    }, 2000);
    return () => clearInterval(t);
  }, [running, loadStatus, onDone]);

  const loadPreview = async (lift = opts.lift_direct_list_bans) => {
    setLoadingPreview(true);
    try { const { data } = await api.get("/admin/discord-reset/preview", { params: { lift_direct_list_bans: lift } }); setPreview(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Preview failed"); }
    finally { setLoadingPreview(false); }
  };

  useEffect(() => { if (preview) loadPreview(opts.lift_direct_list_bans); }, [opts.lift_direct_list_bans]); // eslint-disable-line react-hooks/exhaustive-deps

  const runReset = async () => {
    try {
      const { data } = await api.post("/admin/discord-reset", { ...opts, confirm });
      setJob(data); setRunning(true); setConfirm(""); setPreview(null);
      toast.success("Reset started");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Reset failed"); }
  };

  if (!allowed) return null;
  return (
    <div className="border-2 border-[var(--ff-red)] bg-[var(--ff-red)]/5 p-6 space-y-5" data-testid="discord-reset-panel">
      <div className="flex items-start gap-3">
        <AlertTriangle size={24} className="text-[var(--ff-red)] shrink-0 mt-1" />
        <div>
          <div className="label-eyebrow text-[var(--ff-red)] mb-1">▮ DANGER ZONE</div>
          <h3 className="font-display text-2xl">Reset Discord Bot &amp; Blacklist Data</h3>
          <p className="text-[var(--ff-text-dim)] text-sm max-w-3xl mt-1">
            Restarts the bot, clears its memory caches and wipes all blacklist data (server + user blacklists, ban log,
            moderation history, saved server lists). Lifts only bans the <b>automatic</b> blacklist applied — bans made by hand
            stay. Bot features keep working exactly the same. The whitelist and ban appeals are kept.
          </p>
        </div>
      </div>

      <button onClick={() => loadPreview()} disabled={loadingPreview || running} className="btn-ghost text-xs inline-flex items-center gap-1.5 !px-3 !py-2" data-testid="reset-preview-btn">
        {loadingPreview ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />} {preview ? "Refresh Preview" : "Preview What Will Change"}
      </button>
      {preview && <ResetPreview p={preview} />}

      <ResetOptions opts={opts} setOpts={setOpts} keptCount={preview && !opts.lift_direct_list_bans ? Math.max(preview.kept_direct.length, preview.flags_kept.length) : null} />

      <div className="flex flex-wrap items-center gap-3">
        <input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder='Type "RESET" to confirm' disabled={running}
          className="px-3 py-2 text-sm w-56 font-mono-ui" data-testid="reset-confirm-input" />
        <button onClick={runReset} disabled={confirm !== "RESET" || !preview || running}
          className="btn-primary inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="reset-run-btn">
          {running ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />} Reset Discord Bot &amp; Data
        </button>
        {!preview && !running && <span className="text-xs text-[var(--ff-text-dim)] font-mono-ui">Run a preview first.</span>}
      </div>

      <ResetStatus job={job} running={running} />
    </div>
  );
}
